// @vendors
import React, { useState } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

// @components
import Modal from "../../../../components/Modals/Modal";
import ModalDelet from '../../../../components/Modals/modalDelete/ModalDelete';

// @assets
import emoji from "../../../../assets/icons/emoji _message.svg";

// @store
import useServiceStore from "../../../../store/useServiceStore";
import { useSpinnerStore } from "../../../../store/useSpinnerStore";
import { useCompanyStore } from "../../../../store/useCompanyStore";
import useServiceRequestStore from '../../../../store/useServiceRequestStore';

// @types
import { IServiceById, RequestFilters } from "../../../../types/request.types";
import { ServiceRequestStatus } from "../../../../types/enums.types";
import { formatDate } from "../../../../helpers/date";

const ServiceDetails: React.FC<{
  itemSelected: IServiceById | null,
  setItemSelected: any
}> = ({ itemSelected, setItemSelected }) => {
  const { t } = useTranslation();
  const { updateStatus } = useServiceStore();
  const { openSpinner } = useSpinnerStore();
  const { data } = useCompanyStore();
  const { fetchActiveForProfessional } = useServiceRequestStore();
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const handleCompleteService = async () => {
    if (!itemSelected) return;
    openSpinner(true);
    try {
      await updateStatus(itemSelected?.serviceRequestId, ServiceRequestStatus.pendingCompleted);
      setShowCompleteModal(false);
      openSpinner(false);
      setShowSuccessModal(true);
    } catch (error) {
      setShowCompleteModal(false);
      openSpinner(false);
      toast.error(t("error.postData"));
    }
  };

  const closeModalSuccess = () => {
    setShowSuccessModal(false);
    setItemSelected(null);
    const params: RequestFilters = {
      pageNumber: 1,
      pageSize: 100
    }
    fetchActiveForProfessional(params);
  }

  const getPrice = () => {
    const price = itemSelected?.proposals?.find((item) => item.proposalStatus === 'ACTIVE' || 'COMPLETED');
    if (!price) return '$0'
    const fotmatValue = price.proposalValue.toLocaleString('es-CO', { style: 'currency', currency: 'COP' })
    return fotmatValue;
  }

  return (
    <div className="p-6 bg-cBackground dark:bg-dark-cBackground rounded-lg">
      <div className="w-full flex flex-col md:flex-row items-center gap-[10px] md:gap-[30px]">
        <div className="w-[200px] flex flex-col items-center justify-center gap-[10px]">
          <div className="w-[70px] flex items-center justify-center">
            <img
              src={`https://eu.ui-avatars.com/api/?name=${itemSelected?.customerName || 'User'}`}
              alt={itemSelected?.customerName}
              className="w-[70px] h-[70px] rounded-full object-cover"
            />
          </div>
          <p className="w-full text-[14px] m-0 text-[#444A6D] dark:text-dark-cText flex items-center justify-center">
            {`${itemSelected?.customerName} ${itemSelected?.customer_LastName}`}
          </p>
        </div>

        <div className="w-full flex flex-col sm:flex-row text[#03314B] gap-1 sm:gap-2 items-center justify-center">
          <h2 className="text-[18px] m-0 flex item-center dark:text-dark-cText">
            {t("professional.services.serviceText")}
          </h2>
          <h2 className="text-[18px] m-0 flex item-center dark:text-dark-cText">
            {itemSelected?.serviceRequestNumber}
          </h2>
        </div>
      </div>

      <div className="w-full mt-[20px]">
        <p className="text-[14px] text-[#787878] dark:text-dark-cText/60">
          {t("professional.services.descriptionXervix")}
        </p>
      </div>

      <div className="w-full flex flex-col md:flex-row justify-between gap-[10px] mt-[15px] ">
        <p className="font-medium text-[#3A3A3A] dark:text-dark-cText/80 text-[14px]">
          {t("professional.services.serviceDate")}
        </p>
        <p className="text-[#757575] text-[13px] dark:text-dark-cText/60">
          {formatDate(itemSelected?.serviceNeededBy ?? '')}
        </p>
      </div>

      <div className="w-full flex flex-col md:flex-row justify-between gap-[10px] mt-[15px] ">
        <p className="font-medium text-[#3A3A3A] dark:text-dark-cText/70 text-[14px]">
          {t("professional.services.requestNumber")}
        </p>
        <p className="text-[#757575] dark:text-dark-cText/60 text-[13px] ">
          {itemSelected?.serviceRequestNumber}
        </p>
      </div>

      <div className="w-full mt-[15px] flex flex-col gap-[5px]">
        <label className="font-medium text-[#3A3A3A] dark:text-dark-cText/70 text-[14px]">
          {t("professional.services.serviceName")}
        </label>
        <div className="text-[#666666] dark:text-dark-cText/60 text-[14px] border rounded-md flex p-3">
          {itemSelected?.services[0].name}
        </div>
      </div>

      <div className="w-full mt-[15px] flex flex-col gap-[5px]">
        <label className="font-medium text-[#3A3A3A] dark:text-dark-cText/70 text-[14px]">
          {t("professional.services.priceService")}
        </label>
        <div className="text-[#666666] dark:text-dark-cText/60 text-[14px] border rounded-md flex p-3">
          {getPrice()}
        </div>
      </div>

      <div className="w-full mt-[15px] flex flex-col gap-[5px]">
        <label className="font-medium text-[#3A3A3A] dark:text-dark-cText/70 text-[14px]">
          {t("professional.services.request")}
        </label>
        <div className="text-[#666666] dark:text-dark-cText/60 text-[14px] border rounded-md flex p-3">
          {itemSelected?.description}
        </div>
      </div>

      {(itemSelected?.serviceRequestStatus === "ACTIVE") && (
        <button
          className="w-full mt-[15px] px-4 py-2 bg-bgBrand text-textOnBrand rounded-md"
          onClick={() => setShowCompleteModal(true)}
        >
          {t("professional.services.endService")}
        </button>
      )}

      {/* Modal de confirmación */}
      <ModalDelet
        open={showCompleteModal}
        close={() => setShowCompleteModal(false)}
        title={t("professional.services.messageDelete")}
        submit={handleCompleteService}
      />

      {/* Modal de éxito */}
      <Modal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        title=""
        hasButtonClose={false}
      >
        <div className="min-h-[200px] max-w-[600px] flex flex-col items-center gap-[20px]">
          <img
            src={emoji}
            alt="success operation"
            className="mx-auto mb-4"
            style={{ width: '100px', height: '100px' }}
          />
          <div className="flex flex-col gap-1">
            <p>{t("professional.services.modal.myDear")}, {data?.name ?? ''},</p>
            <p>{t("professional.services.modal.textModalOne")}</p>
            <p>{t("professional.services.modal.textModalTwo")}</p>
            <p>{t("professional.services.modal.textModalThree")}</p>
            <p>{t("professional.services.modal.textModalFour")}</p>
            <p>{t("professional.services.modal.textModalFive")}</p>
          </div>
          <button
            onClick={closeModalSuccess}
            className="bg-[#83B2A3] text-white px-4 py-2 rounded-lg w-[200px]"
          >
            {t("professional.acceptButton")}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default ServiceDetails;
